/**
 * CPU Data Adapter
 *
 * Provides unified data loading for CPU components using GenericDataAdapter pattern.
 * Adapts the new 3-tier architecture to work with existing ComponentDataContext:
 * 1. List files (13 essential fields for list views)
 * 2. Detail files (additional specifications, loaded on-demand)
 * 3. Pricing files (regularPrice, currentPrice)
 *
 * This adapter provides both:
 * - BaseDataLoader-compatible interface (getAllItems, getCurrentItems, getDiscontinuedItems)
 * - Detail loading via GenericDataAdapter (getItemDetail)
 *
 * Usage:
 * ```typescript
 * const adapter = getCPUDataAdapter()
 * const allCPUs = await adapter.getAllItems()           // List view
 * const cpuDetail = await adapter.getItemDetail('id')    // Detail view
 * ```
 */

import { createSingletonAdapter, type PriceInfo, type PricingData } from '../common/GenericDataAdapter'
import type { CPUModel } from '../../models/cpu/cpu-model'
import type { CPUListItem } from '../../models/cpu/cpu-list-model'

/**
 * CPU Data Adapter Class
 *
 * Wraps GenericDataAdapter and provides List/Pricing loading
 */
class CPUDataAdapter {
  private readonly basePath = '/data/cpu'
  private readonly genericAdapter = createSingletonAdapter<CPUModel>({
    basePath: this.basePath,
    componentName: 'cpu',
    pricingTTL: 300000, // 5 minutes
    listTTL: 600000 // 10 minutes
  }).getAdapter()

  private listCache: Map<string, CPUListItem[]> = new Map()
  private pricingCache: Map<string, PriceInfo> = new Map()
  private pricingLoadTime = 0
  private readonly PRICING_TTL = 300000 // 5 minutes

  /**
   * Load list file (current or discontinued)
   */
  private async loadListFile(status: 'current' | 'discontinued'): Promise<CPUListItem[]> {
    const cacheKey = `cpu-${status}-list`

    if (this.listCache.has(cacheKey)) {
      return this.listCache.get(cacheKey)!
    }

    const response = await fetch(`${this.basePath}/cpu-${status}-list.json`)
    if (!response.ok) {
      throw new Error(`Failed to load CPU ${status} list: ${response.statusText}`)
    }

    const data: CPUListItem[] = await response.json()
    this.listCache.set(cacheKey, data)
    return data
  }

  /**
   * Load pricing file (current or discontinued)
   */
  private async loadPricingFile(status: 'current' | 'discontinued'): Promise<Map<string, PriceInfo>> {
    const now = Date.now()

    // Check if pricing data is stale
    if (this.pricingCache.size > 0 && (now - this.pricingLoadTime) < this.PRICING_TTL) {
      return this.pricingCache
    }

    const response = await fetch(`${this.basePath}/cpu-${status}-prices.json`)
    if (!response.ok) {
      console.warn(`Failed to load CPU ${status} pricing: ${response.statusText}`)
      return new Map()
    }

    const data: PricingData = await response.json()
    const priceMap = new Map<string, PriceInfo>()

    if (data?.prices) {
      Object.entries(data.prices).forEach(([id, price]) => {
        priceMap.set(id, price)
      })
    }

    this.pricingLoadTime = now
    return priceMap
  }

  /**
   * Merge list item with pricing data
   */
  private mergeWithPricing(item: CPUListItem, priceInfo?: PriceInfo): CPUModel {
    return {
      ...item,
      regularPrice: priceInfo?.regularPrice ?? null,
      currentPrice: priceInfo?.currentPrice ?? null
    } as CPUModel
  }

  /**
   * Get all CPU items (Current + Discontinued)
   * Compatible with: loader.getAllItems()
   */
  async getAllItems(): Promise<CPUModel[]> {
    const [currentList, discontinuedList, currentPricing, discontinuedPricing] = await Promise.all([
      this.loadListFile('current'),
      this.loadListFile('discontinued'),
      this.loadPricingFile('current'),
      this.loadPricingFile('discontinued')
    ])

    const currentWithPricing = currentList.map(item =>
      this.mergeWithPricing(item, currentPricing.get(item.id))
    )
    const discontinuedWithPricing = discontinuedList.map(item =>
      this.mergeWithPricing(item, discontinuedPricing.get(item.id))
    )

    return [...currentWithPricing, ...discontinuedWithPricing]
  }

  /**
   * Get current CPU items only
   * Compatible with: loader.getCurrentItems()
   */
  async getCurrentItems(): Promise<CPUModel[]> {
    const [listItems, priceMap] = await Promise.all([
      this.loadListFile('current'),
      this.loadPricingFile('current')
    ])

    return listItems.map(item => this.mergeWithPricing(item, priceMap.get(item.id)))
  }

  /**
   * Get discontinued CPU items only
   * Compatible with: loader.getDiscontinuedItems()
   */
  async getDiscontinuedItems(): Promise<CPUModel[]> {
    const [listItems, priceMap] = await Promise.all([
      this.loadListFile('discontinued'),
      this.loadPricingFile('discontinued')
    ])

    return listItems.map(item => this.mergeWithPricing(item, priceMap.get(item.id)))
  }

  /**
   * Get detail data for a specific CPU (with full specifications)
   *
   * Uses GenericDataAdapter for efficient caching
   */
  async getItemDetail(itemId: string): Promise<CPUModel> {
    return this.genericAdapter.getItemDetail(itemId)
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.listCache.clear()
    this.pricingCache.clear()
    this.pricingLoadTime = 0
    this.genericAdapter.clearCache()
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      listCacheSize: this.listCache.size,
      pricingCacheSize: this.pricingCache.size,
      pricingCacheAge: Date.now() - this.pricingLoadTime
    }
  }
}

// Singleton instance
let instance: CPUDataAdapter | null = null

/**
 * Get the singleton CPU data adapter instance
 *
 * @returns {CPUDataAdapter} CPU data adapter
 */
export function getCPUDataAdapter(): CPUDataAdapter {
  instance ??= new CPUDataAdapter()
  return instance
}

/**
 * Reset the CPU data adapter singleton (useful for testing)
 */
export function resetCPUDataAdapter(): void {
  instance = null
}
